<template>
    <div
        class="cz2__swiper-vertical swiper"
        ref="container"
        @scroll="onScroll"
        @keydown="onKey"
        tabindex="0"
    >
        <div class="swiper-wrapper">
            <div
                ref="slide"
                class="swiper-slide"
                :class="{
                    'swiper-slide--kiosk': isKiosk,
                }"
            >
                <slot />
            </div>
        </div>

        <div
            v-if="isKiosk || showScrollbar"
            :class="{ hide: !hasScrollbar || hideScrollbar }"
            class="swiper-scrollbar-wrapper"
        >
            <div
                @click="scrollUp"
                :class="{ cz2__disabled: disableScrollUp }"
                class="scrollbar-button-prev"
            />

            <div
                ref="scrollbar"
                class="swiper-scrollbar cz2__disabled"
            />

            <div
                @click="scrollDown"
                :class="{ cz2__disabled: disableScrollDown }"
                class="scrollbar-button-next"
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
    @import '../../style/variables.scss';

    #cz2.cz2 {
        .cz2__swiper-vertical.swiper {
            width: 100%;

            border: 1px solid transparent;
            &:focus-visible {
                outline: none;
                border: 1px solid #fff;
            }

            .swiper-slide {
                height: auto;
                margin-top: 0px !important;
                border-radius: 10px;
                &.swiper-slide--kiosk {
                    // Prevent touch actions within the element
                    * {
                        touch-action: none;
                    }
                }
            }

            // Scrollbar
            .swiper-scrollbar-wrapper {
                margin: 30px var(--layout-spacing);
                position: absolute;
                top: 0;
                right: 0;
                z-index: 10;
                height: calc(100% - 50px);
                background: rgba(255, 255, 255, 0.3);

                display: flex;
                flex-direction: column;
                justify-content: center;
            }
            .swiper-scrollbar-wrapper,
            .swiper-scrollbar {
                width: 50px;
            }

            .swiper-scrollbar {
                flex: 1 1 auto;
                background-color: transparent;
            }

            .scrollbar-button-prev,
            .scrollbar-button-next {
                width: 50px;
                height: 50px;
                background: rgba(255, 255, 255, 0.8);
                border-radius: 30px;
                display: flex;
                align-items: center;
                justify-content: center;

                &:after {
                    font-size: 30px;
                }

                &.cz2__disabled {
                    opacity: 0.5;
                    pointer-events: none;
                    cursor: not-allowed;
                }
            }

            .scrollbar-button-prev {
                &:after {
                    content: ' \25B2';
                    margin-top: -3px;
                }
            }

            .scrollbar-button-next {
                &:after {
                    content: ' \25BC';
                    margin-top: 3px;
                }
            }
        }
    }

    ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    //   ▗▄▄▖▗▄▄▖ ▗▄▄▄▖▗▖   ▗▖   ▗▄▄▄▖▗▖  ▗▖ ▗▄▄▖    ▗▄▄▖ ▗▄▄▄▖▗▄▄▄▖
    //  ▐▌   ▐▌ ▐▌▐▌   ▐▌   ▐▌     █  ▐▛▚▖▐▌▐▌       ▐▌ ▐▌▐▌   ▐▌
    //   ▝▀▚▖▐▛▀▘ ▐▛▀▀▘▐▌   ▐▌     █  ▐▌ ▝▜▌▐▌▝▜▌    ▐▛▀▚▖▐▛▀▀▘▐▛▀▀▘
    //  ▗▄▄▞▘▐▌   ▐▙▄▄▖▐▙▄▄▖▐▙▄▄▖▗▄█▄▖▐▌  ▐▌▝▚▄▞▘    ▐▙▄▞▘▐▙▄▄▖▐▙▄▄
    ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    #cz2.cz2 {
        .cz2__sb-selector-wrapper {
            .swiper-scrollbar {
                width: 4px;
            }
        }
    }

    #cz2.cz2.cz2__is-sb {
        .swiper-scrollbar {
            right: 2px;

            background: rgba(17, 35, 68, 0.5);

            .swiper-scrollbar-drag {
                background: #E7C267 !important;
            }
        }
    }

    #cz2.cz2.cz2__is-sb.cz2__is-kiosk {
        .swiper-scrollbar {
            right: 2px;

            background: rgba(17, 35, 68, 0.5);

            .swiper-scrollbar-drag {
                background: #E7C267 !important;
            }
        }
    }
</style>

<script>
    // eslint-disable-next-line import/no-unresolved
    import Swiper from 'swiper/bundle';

    export default {
        name: 'Swiper',
        props: {
            topQuerySelector: {
                type: String,
                default: '',
            },

            selectedQuerySelector: {
                type: String,
                default: '',
            },

            showScrollbar: Boolean,
            center: Boolean,

            hideScrollbar: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                swiper: null,
            };
        },

        computed: {
            isKiosk() {
                return this.$store.state.customizer.options.isKiosk;
            },

            disableScrollDown() {
                return this.swiper?.translate === this.swiper?.maxTranslate();
            },

            disableScrollUp() {
                return this.swiper?.translate === 0;
            },

            hasScrollbar() {
                return this.$store.state.hasScrollbar;
            },

            showSubMenu() {
                return this.$store.state.customizer.isSubMenuOpen;
            },
        },

        watch: {
            showSubMenu(newValue) {
                if (newValue) {
                    setTimeout(() => {
                        this.checkForScrollbar();
                    }, 170);

                    this.swiper.setTranslate(0);
                }
            },
        },

        methods: {
            destroySwiper() {
                if (this.swiper) {
                    this.swiper.destroy(true, false);

                    this.swiper = null;
                }
            },

            createSwiper() {
                let lastTranslate = null;

                if (this.swiper) {
                    lastTranslate = this.swiper.translate;

                    this.destroySwiper();
                }

                const swiperOptions = {
                    direction: 'vertical',
                    slidesPerView: 'auto',

                    freeMode: {
                        // Closer to iOS momentum curve.
                        momentumRatio: 0.5,
                        enabled: true,
                    },

                    preventClicks: true,
                    preventClicksPropagation: true,

                    threshold: 10,

                    // Disable accessibility since this is a simple scrollable area.
                    a11y: {
                        enabled: false,
                    },

                    mousewheel: true,

                    pageUpDown: true,

                    preloadImages: false,

                    loop: false,

                    centerInsufficientSlides: this.center,

                    on: {
                        touchStart: () => {
                            this.$store.dispatch('uiInteracted');
                        },

                        setTranslate: () => {
                            this.$emit('scrolled');
                        },

                        touchMove: () => {
                            this.$emit('scrolled');
                        },

                        update: () => {
                            this.checkForScrollbar();
                        },
                    },
                };

                if (this.isKiosk) {
                    swiperOptions.passiveListeners = false;
                    swiperOptions.simulateTouch = false;

                    swiperOptions.scrollbar = {
                        el: this.$refs.scrollbar,
                        draggable: true,
                        hide: false,
                        enabled: true,
                        snapOnRelease: false,
                    };
                } else {
                    swiperOptions.scrollbar = {
                        el: this.$refs.scrollbar,
                        hide: true,
                        enabled: false,
                    };
                }

                this.swiper = new Swiper(this.$refs.container, swiperOptions);

                if (lastTranslate) {
                    this.$nextTick(() => {
                        this.swiper.setTranslate(lastTranslate);
                    });
                }
            },

            scrollToSelected() {
                // Clicking too fast.
                if (this.swiper == null) {
                    return;
                }

                // TODO: Scroll container paddings, should be synced with CSS instead of hardcoding.
                const TOP_PADDING = 24;
                const BOTTOM_PADDING = 16;

                if (!this.selectedQuerySelector?.length || !this.topQuerySelector?.length) {
                    return;
                }

                this.swiper.update();

                const selectedCardEl = this.$el.querySelector(this.selectedQuerySelector);
                const topCardEl = this.$el.querySelector(this.topQuerySelector);

                if (selectedCardEl) {
                    // Base offset.
                    let to = -selectedCardEl.offsetTop;

                    // Top card offset.
                    const topCard = -topCardEl.offsetTop;

                    // If the top card, scroll to the top of the scrollable area to show any accordions and top margins.
                    if (to === topCard) {
                        to = 0;
                    } else {
                        // .selection-card-list has a "natural padding" of 24px. Use that to back off the scroll position a bit.
                        to += TOP_PADDING;
                    }

                    // Don't overscroll on the top.
                    if (to > 0) {
                        to = 0;
                    }

                    // Don't overscroll on the bottom.
                    // eslint-disable-next-line prefer-destructuring
                    const scrollHeight = this.$refs.slide.scrollHeight;
                    const containerHeight = this.$refs.container.offsetHeight;

                    // In some cases, scroll padding (to avoid navigation) is added to swiper itself.
                    const containerStyle = window.getComputedStyle(this.$refs.container);
                    let containerPaddingBottom = parseInt(containerStyle.paddingBottom, 10);
                    if (containerPaddingBottom === 0) {
                        // Use default spacing.
                        containerPaddingBottom = BOTTOM_PADDING;
                    }

                    let bottomLimit = scrollHeight - containerHeight + containerPaddingBottom;
                    if (bottomLimit < 0) {
                        bottomLimit = 0;
                    }

                    if (to < -bottomLimit) {
                        to = -bottomLimit;
                    }

                    this.swiper.setTranslate(to);
                }
            },

            resize() {
                if (this.swiper) {
                    this.swiper.update();
                }
            },

            onScroll() {
                // Force no-scroll manually, since overflow: clip is causing issues.
                if (this.$refs.container) {
                    if (this.$refs.container.scrollTop !== 0) {
                        this.$refs.container.scrollTop = 0;
                    }
                }
            },

            onKey(e) {
                if (this.swiper) {
                    const scrollHeight = this.$refs.container.querySelector('.swiper-slide').offsetHeight;
                    const wrapperHeight = this.$refs.container.querySelector('.swiper-wrapper').offsetHeight;
                    const scrollLimit = (scrollHeight - wrapperHeight) * -1;
                    const swiperTranslate = this.swiper.translate;
                    const scrollDistance = 20;

                    if (e.key === 'ArrowUp' && !(swiperTranslate >= 0)) {
                        this.swiper.setTranslate(swiperTranslate + scrollDistance);
                    } else if (e.key === 'ArrowDown' && !(swiperTranslate <= scrollLimit)) {
                        this.swiper.setTranslate(swiperTranslate - scrollDistance);
                    }
                }
            },

            scrollDown() {
                const swiperContainerHeight = this.swiper.el.offsetHeight;
                const currentTranslate = this.swiper.translate;
                const maxTranslate = this.swiper.maxTranslate();
                let scrollDistance = (swiperContainerHeight * -1) + currentTranslate;

                if (scrollDistance < maxTranslate) {
                    scrollDistance = maxTranslate;
                }

                this.swiper.translateTo(scrollDistance, 250);
            },

            scrollUp() {
                const swiperContainerHeight = this.swiper.el.offsetHeight;
                const currentTranslate = this.swiper.translate;
                const maxTranslate = 0;
                let scrollDistance = swiperContainerHeight + currentTranslate;

                if (scrollDistance > maxTranslate) {
                    scrollDistance = maxTranslate;
                }

                this.swiper.translateTo(scrollDistance, 250);
            },

            checkForScrollbar() {
                const scrollbarHidden = this.swiper?.scrollbar?.$el?.hasClass('swiper-scrollbar-lock');

                this.$store.dispatch('setHasScrollbar', !scrollbarHidden);
            },
        },

        destroyed() {
            this.destroySwiper();

            if (this.observer) {
                this.observer.disconnect();
            }
        },

        mounted() {
            this.createSwiper();

            // Monitor the slide size.
            // Changes when:
            // - Container resize
            // - Accordion state updates
            this.observer = new ResizeObserver(() => {
                this.resize();
            });

            this.observer.observe(this.$refs.slide);

            setTimeout(() => {
                this.scrollToSelected();
            }, 100);
        },
    };
</script>
